// /home/coder/project/frontend/src/pages/ToCompCreate.tsx

import React from 'react';
import { useNavigate } from 'react-router-dom';
// highlight-start
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGetCountries } from '../../../hooks/genericHooks'; // Import hook
// highlight-end
import { Form, Card, Space, message, Typography, Row, Col, Input, Select } from 'antd';
import { SaveOutlined, RollbackOutlined, BankOutlined as CompanyIcon } from '@ant-design/icons';
import { insertData } from '../../../services/api';
import type { Company } from '../../../types'; 
import { ProtectedRoute, PageGuard } from '../../../components/common/security/ProtectedRoute';
import Button from '../../../components/common/Button';

const { Title } = Typography;
const { Option } = Select;

// --- Relation Mapping and Helper ---
const relationOptions = [
  { value: 1, label: 'Root' }, { value: 2, label: 'Partner' }, { value: 3, label: 'Client' },
  { value: 11, label: 'Root Subcon' }, { value: 12, label: 'Root Vendor' },
  { value: 21, label: 'Partner Subcon' }, { value: 22, label: 'Partner Vendor' },
  { value: 33, label: 'Client Subcon' }, { value: 34, label: 'Client Vendor' },
  { value: 35, label: 'Other' },
];
// -----------------------------------

// Interface for form values, derived from Company type
interface CompanyFormValues {
  name: string;
  relation: number;
  country_code: string; // Added country code
  address?: string;
  phone?: string;
  email?: string;
  status: 'active' | 'inactive' | 'terminated';
  category: 'service' | 'goods' | 'both';
  area: 'Electric' | 'Mechanic' | 'Construction' | 'Design' | 'QAQC' | 'HSE' | 'PRECOMM' | 'Customs' | 'Transport' | 'Utility' | 'Administration' | 'Consultancy' | 'State Authority' | 'Other';
  short_code: string; // Added short_code
}

// --- Enum Options for Select dropdowns ---
const statusOptions: CompanyFormValues['status'][] = ['active', 'inactive', 'terminated'];
const categoryOptions: CompanyFormValues['category'][] = ['service', 'goods', 'both'];
const areaOptions: CompanyFormValues['area'][] = [
  'Electric', 'Mechanic', 'Construction', 'Design', 'QAQC', 'HSE', 'PRECOMM',
  'Customs', 'Transport', 'Utility', 'Administration', 'Consultancy',
  'State Authority', 'Other'
];
// -----------------------------------------

// Helper function to format enum strings (simple capitalize)
const formatEnumString = (value: string | undefined | null): string => {
    if (!value) return '-';
    const strValue = String(value);
    return strValue.charAt(0).toUpperCase() + strValue.slice(1);
};

const ToCompCreate: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<CompanyFormValues>();
  const queryClient = useQueryClient();

  // highlight-start
  // --- Data Fetching (Countries) ---
  const { data: countriesData, isLoading: isLoadingCountries, isError: isErrorCountries, error: errorCountries } = useGetCountries();
  const countries = countriesData?.data || [];
  // highlight-end

  // --- Mutation for Creating Data ---
  const createCompanyMutation = useMutation({
    mutationFn: (newData: Partial<Company>) => insertData('to_companies', newData),
    onSuccess: (data) => {
      const newId = data?.id ?? 'N/A';
      message.success(`Company "${form.getFieldValue('name') || 'New Company'}" created successfully! (ID: ${newId})`);
      queryClient.invalidateQueries({ queryKey: ['to_companies'] });
      queryClient.refetchQueries({ queryKey: ['to_companies'] }); // Force refresh persisted list
      navigate('/to/companies');
    },
    onError: (error: any) => {
      console.error(`Error creating company:`, error);
      const errorMsg = error.response?.data?.error || error.message || 'An unknown error occurred.';
      message.error(`Error creating company: ${errorMsg}`);
    },
  });

  // --- Form Submission Logic ---
  const handleSubmit = (values: CompanyFormValues) => {
    const formattedData: Partial<Company> = {
      name: values.name,
      relation: values.relation,
      country_code: values.country_code, // Include country code
      address: values.address ?? undefined,
      phone: values.phone ?? undefined,
      email: values.email ?? undefined,
      status: values.status,
      category: values.category,
      area: values.area,
      short_code: values.short_code, // Include short_code in submission
    };

    console.log("Submitting New Company Data:", JSON.stringify(formattedData, null, 2));
    createCompanyMutation.mutate(formattedData);
  };

  const onFinish = (values: CompanyFormValues) => {
    handleSubmit(values);
  };

  // --- Define Form Item Components ---
  const nameItem = (
    <Form.Item name="name" label="Company Name" rules={[{ required: true, message: 'Please enter the company name' }]}>
      <Input placeholder="Enter company name" />
    </Form.Item>
  );
  const relationItem = (
    <Form.Item
      name="relation"
      label="Relation"
      rules={[{ required: true, message: 'Please select the relation type' }]}
    >
      <Select
        placeholder="Select relation type"
        allowClear={false}
        showSearch
        filterOption={(input, option) =>
          (String(option?.label ?? '')).toLowerCase().includes(input.toLowerCase())
        }
      >
        {relationOptions.map(opt => (
          <Option key={opt.value} value={opt.value} label={opt.label}>
            {opt.label}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
  // highlight-start
  const countryItem = (
     <Form.Item name="country_code" label="Country" rules={[{ required: true, message: 'Please select the country' }]}>
      <Select
        placeholder="Select country"
        loading={isLoadingCountries} // Show loading indicator on the select itself
        showSearch
        allowClear={false} // Assuming country is required
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
        options={countries.map(country => ({
          value: country.country_code,
          label: country.name
        }))}
      />
    </Form.Item>
  );
  // highlight-end
  const addressItem = (
    <Form.Item name="address" label="Address">
      <Input.TextArea rows={2} placeholder="Enter company address" />
    </Form.Item>
  );
  const phoneItem = (
    <Form.Item name="phone" label="Phone">
      <Input placeholder="Enter phone number" />
    </Form.Item>
  );
  const emailItem = (
    <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Please enter a valid email' }]}>
      <Input placeholder="Enter email address" />
    </Form.Item>
  );
  const statusItem = (
    <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Please select the status' }]} initialValue="active">
      <Select placeholder="Select status" allowClear={false}>
        {statusOptions.map(status => (<Option key={status} value={status}>{formatEnumString(status)}</Option>))}
      </Select>
    </Form.Item>
  );
  const categoryItem = (
    <Form.Item name="category" label="Category" rules={[{ required: true, message: 'Please select the category' }]}>
      <Select placeholder="Select category" allowClear={false}>
        {categoryOptions.map(cat => (<Option key={cat} value={cat}>{formatEnumString(cat)}</Option>))}
      </Select>
    </Form.Item>
  );
  const areaItem = (
    <Form.Item name="area" label="Area" rules={[{ required: true, message: 'Please select the area' }]}>
      <Select
        placeholder="Select area of occupation"
        allowClear={false}
        showSearch
        filterOption={(input, option) =>
          (String(option?.label ?? '')).toLowerCase().includes(input.toLowerCase())
        }
      >
        {areaOptions.map(area => (
          <Option key={area} value={area} label={formatEnumString(area)}>
            {formatEnumString(area)}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
  const shortCodeItem = (
    <Form.Item
      name="short_code"
      label="Short Code"
      rules={[
        { required: true, message: 'Please enter the 3-letter short code' },
        { len: 3, message: 'Short code must be exactly 3 characters' },
        { pattern: /^[A-Za-z0-9]+$/, message: 'Short code must be alphanumeric' },
      ]}
    >
      <Input placeholder="e.g. ABC" maxLength={3} />
    </Form.Item>
  );
  // --- End of Form Item Definitions ---

  // --- Component Rendering (JSX) ---
  return (
      <PageGuard>
        <Card>
          {/* Header */}
          <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                <CompanyIcon style={{ marginRight: 8 }} /> Create New Company
              </Title>
            </Col>
            <Col>
              <Space>
                {/* highlight-next-line */}
                <Button icon={<RollbackOutlined />} onClick={() => navigate('/to/companies')} disabled={isLoadingCountries || createCompanyMutation.isPending}>
                  Cancel
                </Button>
                {/* highlight-next-line */}
                <Button action="add" type="primary" icon={<SaveOutlined />} loading={isLoadingCountries || createCompanyMutation.isPending} onClick={() => form.submit()}>
                  Create Company
                </Button>
              </Space>
            </Col>
          </Row>

          {/* Form */}
          {/* highlight-next-line */}
          <Form form={form} layout="vertical" onFinish={onFinish} disabled={isLoadingCountries || createCompanyMutation.isPending}>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>{nameItem}</Col>
              <Col xs={24} sm={12} md={8}>{relationItem}</Col>
              {/* highlight-next-line */}
              <Col xs={24} sm={12} md={8}>{countryItem}</Col> {/* Added Country */}
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>{phoneItem}</Col>
              <Col xs={24} sm={12} md={8}>{emailItem}</Col>
              <Col xs={24} sm={12} md={8}>{statusItem}</Col> {/* Moved Status here */}
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>{categoryItem}</Col>
              <Col xs={24} sm={12} md={8}>{areaItem}</Col>
              <Col xs={24} sm={12} md={8}>{shortCodeItem}</Col> {/* Moved Short Code input here */}
            </Row>
            <Row gutter={16}
              style={{ marginTop: 8 }}>
              <Col xs={24} sm={24} md={8}>{addressItem}</Col> {/* Moved Address here */}
            </Row>
          </Form>
        </Card>
      </PageGuard>
  );
};

export default ToCompCreate;
